
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Clock, Play, Zap, TrendingUp, Camera, Calendar, Target, Users, Crown, Star, Settings as SettingsIcon, BarChart3, ChevronRight, ShoppingBag } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { SUBSCRIPTION_CONSTANTS, SHOPIFY_CONFIG } from '../config/appConfig';
import logger from '../utils/logger';

const Dashboard = ({ setView }) => {
  const navigate = useNavigate();
  const { user, userProfile, userSubscription } = useUser();

  const getSubscriptionBadge = () => {
    if (!userSubscription?.subscriptionPlan) return null;

    const dbPlan = userSubscription.subscriptionPlan;

    const plan = SUBSCRIPTION_CONSTANTS.getPlanByDbId(dbPlan);
    if (!plan) return null;

    const badges = {
      basic: {
        gradient: 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20',
        text: 'text-blue-400',
        border: 'border-blue-500/40',
        glow: 'shadow-lg shadow-blue-500/20',
        icon: Zap
      },
      trackaipro: {
        gradient: 'bg-gradient-to-r from-green-500/20 to-emerald-500/20',
        text: 'text-green-400',
        border: 'border-green-500/40',
        glow: 'shadow-lg shadow-green-500/20',
        icon: Star
      },
      cricketxpro: {
        gradient: 'bg-gradient-to-r from-purple-500/20 to-pink-500/20',
        text: 'text-purple-400',
        border: 'border-purple-500/40',
        glow: 'shadow-lg shadow-purple-500/20',
        icon: Crown
      }
    };

    const badge = badges[dbPlan];
    if (!badge) return null;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-3.5 py-1.5 rounded-lg text-xs font-bold border backdrop-blur-sm ${badge.gradient} ${badge.text} ${badge.border} ${badge.glow}`}>
        <Icon size={13} className="mr-1.5" />
        {plan.name}
      </span>
    );
  };

  return (
    <div className="flex flex-col h-full bg-gray-900 overflow-y-auto">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-900 to-black overflow-hidden min-h-[200px]">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-64 h-64">
            <Activity size={200} className="text-green-500" />
          </div>
        </div>

        {/* Top Bar */}
        <div className="relative z-20 flex items-start justify-between p-4 pt-6">
          {/* Left side - empty space for global back button */}
          <div className="w-14"></div>

          {/* Welcome Section - Right Side */}
          <div className="text-right flex-1 flex items-start justify-between">
            <div className="text-right">
              <p className="text-green-400 font-semibold text-xs uppercase tracking-widest mb-1.5">
                Welcome Back
              </p>
              <div className="flex justify-end">
                {getSubscriptionBadge()}
              </div>
            </div>

            {/* Settings Button - Moved to top-right */}
            <button
              onClick={() => navigate('/settings')}
              className="p-2.5 bg-gray-800/80 hover:bg-gray-700 rounded-xl backdrop-blur-sm transition-all hover:scale-105 shadow-lg ml-4"
            >
              <SettingsIcon size={20} className="text-gray-300" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 px-6 pb-8 pt-6">
          <h1 className="text-4xl font-extrabold text-white mb-1 tracking-tight">
            {userProfile?.displayName || user?.displayName || 'Cricket Player'}
          </h1>
          <p className="text-gray-400 text-sm font-medium">Let's get to work.</p>
        </div>
      </div>

      {/* Daily Focus Card (Section 5.3) */}
      <div className="px-4 -mt-4 relative z-20 mb-6">
        <div className="bg-gray-800/90 backdrop-blur-sm rounded-2xl p-6 shadow-2xl border border-gray-700/50">
          <div className="flex justify-between items-start mb-5">
            <div>
              <h3 className="text-white font-bold text-xl mb-1">Today's Focus</h3>
              <p className="text-gray-400 text-xs">Microcycle: Week 2, Day 3</p>
            </div>
            <div className="bg-purple-500/20 p-2.5 rounded-xl border border-purple-500/30">
              <Calendar size={20} className="text-purple-400" />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center p-4 bg-gray-900/60 rounded-xl border border-gray-700/50 hover:border-gray-600/50 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mr-3 border border-blue-500/30">
                <Zap size={16} className="text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mb-0.5">Morning</p>
                <p className="text-sm text-white font-semibold">Visualization & Mobility</p>
              </div>
              <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center border border-green-500/30">
                <div className="w-2.5 h-2.5 bg-green-500 rounded-full"></div>
              </div>
            </div>

            <div className="flex items-center p-4 bg-gradient-to-r from-purple-900/50 to-gray-900/60 rounded-xl border border-purple-500/40 shadow-lg shadow-purple-500/10">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 to-pink-500/20 flex items-center justify-center mr-3 border border-purple-500/40">
                <Activity size={16} className="text-purple-300" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] text-purple-300 uppercase font-bold tracking-wider mb-0.5">Training (Now)</p>
                <p className="text-sm text-white font-semibold">Net Session: Off-Stump Line</p>
              </div>
              <button
                onClick={() => setView('train')}
                className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white p-2.5 rounded-xl transition-all hover:scale-105 shadow-lg shadow-purple-500/30"
              >
                <Play size={15} fill="currentColor" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Progressive Disclosure Dashboard (Section 7.3) */}
      <div className="px-4 pb-8 space-y-6">

        {/* Level 1: Summary Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-5 rounded-2xl border border-gray-700/50 shadow-xl hover:border-green-500/30 transition-colors">
            <p className="text-gray-400 text-xs font-medium mb-2 uppercase tracking-wider">Arm Speed</p>
            <h4 className="text-3xl font-extrabold text-white mb-2">2100<span className="text-sm text-gray-500 font-normal ml-1">°/s</span></h4>
            <div className="flex items-center text-green-400 text-xs font-semibold bg-green-500/10 px-2 py-1 rounded-lg w-fit">
              <TrendingUp size={12} className="mr-1" /> +5% vs Last Wk
            </div>
          </div>
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-5 rounded-2xl border border-gray-700/50 shadow-xl hover:border-yellow-500/30 transition-colors">
            <p className="text-gray-400 text-xs font-medium mb-2 uppercase tracking-wider">Consistency</p>
            <h4 className="text-3xl font-extrabold text-white mb-2">82<span className="text-sm text-gray-500 font-normal ml-1">%</span></h4>
            <div className="flex items-center text-yellow-400 text-xs font-semibold bg-yellow-500/10 px-2 py-1 rounded-lg w-fit">
              <TrendingUp size={12} className="mr-1" /> Good Length
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center mb-4">
            <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 p-2 rounded-lg border border-cyan-500/30 mr-3">
              <Zap size={18} className="text-cyan-400" />
            </div>
            <h3 className="text-white font-bold text-xl">Quick Actions</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setView('bio')}
              className="bg-gradient-to-br from-gray-800 to-gray-900 p-5 rounded-2xl border border-gray-700/50 hover:border-blue-500/50 transition-all text-left group shadow-xl hover:shadow-blue-500/20 hover:scale-105"
            >
              <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 w-12 h-12 rounded-xl flex items-center justify-center mb-3 group-hover:from-blue-500 group-hover:to-cyan-500 transition-all border border-blue-500/30 group-hover:border-blue-400 shadow-lg shadow-blue-500/20">
                <Camera size={22} className="text-blue-400 group-hover:text-white transition-colors" />
              </div>
              <h4 className="text-white font-bold text-base mb-1">Analyze</h4>
              <p className="text-gray-400 text-xs">Upload Video</p>
            </button>

            <button
              onClick={() => setView('tactics')}
              className="bg-gradient-to-br from-gray-800 to-gray-900 p-5 rounded-2xl border border-gray-700/50 hover:border-orange-500/50 transition-all text-left group shadow-xl hover:shadow-orange-500/20 hover:scale-105"
            >
              <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 w-12 h-12 rounded-xl flex items-center justify-center mb-3 group-hover:from-orange-500 group-hover:to-red-500 transition-all border border-orange-500/30 group-hover:border-orange-400 shadow-lg shadow-orange-500/20">
                <Target size={22} className="text-orange-400 group-hover:text-white transition-colors" />
              </div>
              <h4 className="text-white font-bold text-base mb-1">Tactics</h4>
              <p className="text-gray-400 text-xs">Plan Field</p>
            </button>
          </div>
        </div>

        {/* Shop Gear Section */}
        <div>
          <div className="flex items-center mb-4">
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 p-2 rounded-lg border border-green-500/30 mr-3">
              <ShoppingBag size={18} className="text-green-400" />
            </div>
            <h3 className="text-white font-bold text-xl">Shop Gear</h3>
          </div>
          <a
            href={SHOPIFY_CONFIG.getShopLink(user?.email)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              const shopLink = SHOPIFY_CONFIG.getShopLink(user?.email);
              logger.debug('[Dashboard] Shop Now clicked:', { email: user?.email, link: shopLink });
              if (!user?.email) {
                logger.debug('[Dashboard] No user email available for shop link');
              }
            }}
            className="block bg-gradient-to-br from-gray-800 to-gray-900 p-5 rounded-2xl border border-gray-700/50 hover:border-green-500/50 transition-all text-left group shadow-xl hover:shadow-green-500/20 hover:scale-105"
          >
            <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 w-12 h-12 rounded-xl flex items-center justify-center mb-3 group-hover:from-green-500 group-hover:to-emerald-500 transition-all border border-green-500/30 group-hover:border-green-400 shadow-lg shadow-green-500/20">
              <ShoppingBag size={22} className="text-green-400 group-hover:text-white transition-colors" />
            </div>
            <h4 className="text-white font-bold text-base mb-1">Shop Cricket Gear</h4>
            <p className="text-gray-400 text-xs">Bats, gloves, and more</p>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

